import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebounce from 'src/common/hooks/useDebounce';
import useLocalStorageState from 'src/common/hooks/useLocalStorageState';
import { LocalStorageKeys, LocalStorageValues } from 'src/types/common';

export interface SearchContextValue {
  searchTerm: string;
  handleSearchChange: (search: string) => void;
  isChanging: boolean;
  debouncedSearchTerm: string;
  clearSearch: () => void;
  searchHistory: LocalStorageValues[LocalStorageKeys.SearchHistory];
  filteredSearchHistory: LocalStorageValues[LocalStorageKeys.SearchHistory];
}

export const SearchContext = createContext<SearchContextValue>({
  handleSearchChange: () => {},
  searchTerm: '',
  clearSearch: () => {},
  isChanging: false,
  debouncedSearchTerm: '',
  searchHistory: [],
  filteredSearchHistory: [],
});

interface SearchProviderProps extends React.PropsWithChildren {
  searchKey?: string;
}

export const SearchProvider = ({
  children,
  searchKey = 'search',
}: SearchProviderProps) => {
  const [URLSearchParams, SetURLSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const { debouncedValue, isChanging } = useDebounce({
    value: searchTerm,
    delay: 300,
  });

  useEffect(() => {
    const searchTermFromUrl = URLSearchParams.get(searchKey);

    if (searchTermFromUrl) {
      setSearchTerm((prev) => {
        const isNewSearch = searchTermFromUrl !== prev;

        if (isNewSearch) {
          return searchTermFromUrl;
        }

        return prev;
      });
    }
  }, [URLSearchParams, searchKey]);

  const [history, setHistory] = useLocalStorageState(
    LocalStorageKeys.SearchHistory,
    []
  );

  useEffect(() => {
    if (debouncedValue) {
      setHistory((prevHistory) => {
        const newHistory = prevHistory ? [...prevHistory] : [];
        const lowerCaseDebouncedValue = debouncedValue.toLowerCase();

        const existingIndex = newHistory.findIndex(
          (item) => item.toLowerCase() === lowerCaseDebouncedValue
        );

        if (existingIndex > -1) {
          newHistory.splice(existingIndex, 1);
        }

        newHistory.unshift(debouncedValue);

        return newHistory.slice(0, 8);
      });
    }
  }, [debouncedValue, setHistory]);

  const filteredSearchHistory = useMemo(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return history
      ?.filter((item) => item.toLowerCase().includes(lowerCaseSearchTerm))
      ?.filter(
        (item, _, arr) =>
          arr.length > 1 || item.toLowerCase() !== lowerCaseSearchTerm
      );
  }, [history, searchTerm]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    SetURLSearchParams((prev) => {
      prev.delete('search');
      return prev;
    });
  };

  return (
    <SearchContext.Provider
      value={{
        searchTerm,
        handleSearchChange,
        filteredSearchHistory,
        searchHistory: history,
        clearSearch: handleSearchClear,
        isChanging,
        debouncedSearchTerm: debouncedValue,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
